import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import { EventName, PerformanceMetric } from '../../../constants/telemetry';
import {
    GetDevBoxResponse,
    RestoreSnapshotResponse,
    restoreSnapshot,
} from '../../../data/services/data-plane-api/dev-box';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import { getDevBoxState } from '../../../utilities/dev-box';
import { createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault } from '../../../utilities/failure';
import { trackEvent, trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import { createOptionsForDataPlaneResourceMetric } from '../../../utilities/telemetry/helpers';
import { listDevBoxOperations } from '../../actions/dev-box-operation/dev-box-operation-action-creators';
import {
    pollNonTerminalDevBox,
    restoreSnapshotAccepted,
    restoreSnapshot as restoreSnapshotActionCreator,
    restoreSnapshotError,
    restoreSnapshotFailed,
    restoreSnapshotSuccess,
} from '../../actions/dev-box/dev-box-action-creators';
import { RestoreSnapshotAcceptedAction, RestoreSnapshotAction } from '../../actions/dev-box/dev-box-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { takeEvery, takeLeading } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';
import { DevBoxState } from '../../store/dev-box-state';

export function* restoreSnapshotSaga(action: RestoreSnapshotAction): SagaIterator {
    const startTime = new Date();
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id, snapshotId } = payload;

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );

        const response: RestoreSnapshotResponse = yield call(restoreSnapshot, id, accessToken, snapshotId, activityId);

        if (isFailureResponse(response)) {
            yield put(restoreSnapshotFailed({ failure: response, id }, meta));

            yield call(
                trackTimedPerformanceMetric,
                PerformanceMetric.RestoreSnapshot,
                startTime,
                AsyncOutcome.Failed,
                createOptionsForDataPlaneResourceMetric(id, activityId, response.code)
            );

            return;
        }

        yield put(restoreSnapshotAccepted({ id }, meta));

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.RestoreSnapshot,
            startTime,
            AsyncOutcome.Success,
            createOptionsForDataPlaneResourceMetric(id, activityId)
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.RestoreSnapshot);
        yield put(restoreSnapshotError({ error, id }, meta));

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.RestoreSnapshot,
            startTime,
            AsyncOutcome.Error,
            createOptionsForDataPlaneResourceMetric(id, activityId, error.code)
        );
    }
}

export function* restoreSnapshotAcceptedSaga(action: RestoreSnapshotAcceptedAction): SagaIterator {
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id } = payload;

    try {
        const response: GetDevBoxResponse = yield putAndAwait(pollNonTerminalDevBox({ id }, meta));

        if (isFailureResponse(response)) {
            yield put(restoreSnapshotFailed({ failure: response, id }, meta));
            return;
        }

        const { data } = response;
        const { actionState } = data;
        const state = getDevBoxState(data);

        switch (actionState) {
            case DevBoxState.Restored:
                yield put(listDevBoxOperations({ id }));
                yield put(restoreSnapshotSuccess({ id, result: data }, meta));
                break;
            default:
                // Unexpected state: log cases where we're falling back on the default error message. This means we're
                // in a failed state, but there are no error details.
                if (!data.error) {
                    trackEvent(EventName.DevBoxInFailedStateWithNoError, {
                        activityId,
                        properties: {
                            actionType: action.type,
                            id,
                            provisioningState: data.provisioningState,
                            devBoxState: state,
                        },
                    });
                }

                const failure = createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
                    data.error,
                    FailureOperation.RestoreSnapshot
                );

                yield put(restoreSnapshotFailed({ failure, id, result: data }, meta));
                break;
        }
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.RestoreSnapshot);
        yield put(restoreSnapshotError({ error, id }, meta));
    }
}

export function* restoreSnapshotListenerSaga(): SagaIterator {
    yield takeLeading(restoreSnapshotActionCreator, restoreSnapshotSaga);
}

export function* restoreSnapshotAcceptedListenerSaga(): SagaIterator {
    yield takeEvery(restoreSnapshotAccepted, restoreSnapshotAcceptedSaga);
}
